import { buildAnnexPath } from './annex-path';

/**
 * Cross-book reference cue rules.
 *
 * The auto-detector resolves "Rule 5.2"-style mentions positionally against
 * the document being imported. Some mentions, however, point OUTSIDE the
 * current book — "Section 1 from the Constitution", "paragraphs 1. (c), (d)
 * and (e) of the World Rankings Competition definition". Those carry a cue
 * phrase right after the numbered mention that identifies where the target
 * actually lives.
 *
 * This module is the semi-hardcoded map of such cue phrases. Each rule pairs
 * a cue `pattern` with what the cue means:
 *
 *  - `type: 'book'`         — the mention belongs to another imported book.
 *                              `bookHint` is matched (case-insensitive
 *                              substring) against `DocumentBook.name` to find
 *                              the target book.
 *  - `type: 'internal-link'` — the mention points to a predefined location
 *                              (e.g. a definitions annex page). `target` is
 *                              persisted as the reference's external URL /
 *                              app path verbatim.
 *
 * Both rule kinds may carry `targetFragment`: a text snippet identifying the
 * exact spot INSIDE the target (a part of a paragraph, e.g. the defined term
 * itself). It is persisted on the Reference row so navigation can highlight
 * the fragment rather than just the containing block.
 *
 * Curation notes:
 *  - Patterns are matched case-insensitively against plain text (HTML and
 *    existing {{ref:…}} tokens already stripped by the detector). Do NOT use
 *    the `g` flag — `findCrossBookCue` normalises it away anyway.
 *  - The cue must follow the numbered mention almost immediately (only
 *    whitespace/commas/semicolons in between, capped at MAX_CUE_GAP chars),
 *    so a cue later in the sentence never hijacks an unrelated mention.
 *  - If the "World Rankings Competition" definitions end up imported as a
 *    separate book instead of an annex page, flip that rule to
 *    `{ type: 'book', bookHint: '…' }` — nothing else needs to change.
 *  - The World Rankings Competition definition cue has both an EN rule
 *    (tolerating singular/plural "Competition(s)") and a separate FR rule
 *    (its own cue phrase, targeting the annex with `?language=FR`) — they
 *    are independent entries, not one rule with alternation, so each can be
 *    tuned without risking the other.
 */

export type CrossBookBookRule = {
  type: 'book';
  /** Cue phrase that marks the mention as belonging to another book. */
  pattern: RegExp;
  /** Case-insensitive substring matched against `DocumentBook.name`. */
  bookHint: string;
  /**
   * Case-insensitive substring matched against the target root `style='book'`
   * container heading. Defaults to `bookHint` when omitted — use a distinct
   * value when the imported root heading is localized (e.g. FR "Règles de
   * compétition") but `DocumentBook.name` stays filename-derived English.
   */
  rootHint?: string;
  /** Optional partial-paragraph anchor inside the target block. */
  targetFragment?: string;
};

export type CrossBookLinkRule = {
  type: 'internal-link';
  /** Cue phrase that marks the mention as pointing at a predefined link. */
  pattern: RegExp;
  /** Predefined link target — absolute URL or in-app path (with #anchor). */
  target: string;
  /** Optional partial-paragraph anchor inside the target page/block. */
  targetFragment?: string;
};

export type CrossBookRule = CrossBookBookRule | CrossBookLinkRule;

export type CrossBookCueMatch = {
  rule: CrossBookRule;
  /** Index in the scanned text where the cue starts. */
  start: number;
  /** Index just past the end of the cue. */
  end: number;
};

/**
 * The cue must start within this many characters of the mention's end, and
 * the gap may contain only separators (see CUE_GAP_RE). Sized for tails like
 * `") "` after a subletter list — generous enough for stray punctuation,
 * tight enough that a cue in the NEXT clause never attaches.
 */
export const MAX_CUE_GAP = 16;

// Separators only, PLUS an optional literal "inclus"/"incluse"/"incluses" —
// the WA French corpus writes range citations as "Règles 17.8 à 17.13
// incluses des Règles techniques" ("Rules 17.8 to 17.13 inclusive of the
// Technical Rules"), inserting that one word between the range end and the
// cue. Without it the gap check rejects the cue and the whole range is left
// unresolved. Case-insensitive so a de-accented "inclus" still matches; can
// only WIDEN the gap match (the pure-separator form still matches when the
// word is absent), so it cannot regress any existing cue.
const CUE_GAP_RE = /^[\s,;]*(?:inclus(?:es?)?[\s,;]*)?$/i;

export const CROSS_BOOK_RULES: readonly CrossBookRule[] = [
  {
    // "Section 1 from the Constitution", "Article 29 of the Constitution",
    // "Part IV under the Constitution".
    type: 'book',
    pattern: /\b(?:from|of|under)\s+(?:the\s+)?Constitution\b/i,
    bookHint: 'Constitution',
  },
  {
    // "Rule 5.2 of the Technical Rules" — the Technical Rules book (second
    // book of the compound C1.1 & C2.1 import; resolveBookHint scopes the
    // name map to the matching root book container).
    type: 'book',
    pattern: /\bof\s+the\s+Technical\s+Rules\b/i,
    bookHint: 'Technical Rules',
  },
  {
    // "Rule 1.5 of the Competition Rules" — first book of the compound
    // C1.1 & C2.1 import.
    type: 'book',
    pattern: /\bof\s+the\s+Competition\s+Rules\b/i,
    bookHint: 'Competition Rules',
  },
  {
    // FR: "voir Règle 14 des Règles de compétition" — same compound doc,
    // localized root heading; book name is filename-derived French.
    type: 'book',
    pattern: /\bdes\s+R[èe]gles\s+de\s+comp[ée]tition\b/i,
    bookHint: 'Règles des Compétitions',
    rootHint: 'Règles de compétition',
  },
  {
    // FR: "des Règles techniques" — cross-sub-book cite inside the compound.
    type: 'book',
    pattern: /\bdes\s+R[èe]gles\s+techniques\b/i,
    bookHint: 'Règles Techniques',
    rootHint: 'Règles techniques',
  },
  {
    // "paragraphs 1. (c), (d) and (e) of the World Rankings Competition
    // definition" — links to wherever the definition lives. Currently a
    // predefined annex anchor; flip to `type: 'book'` if the definitions
    // get imported as their own book (see module doc above). "Competitions"
    // (plural) also appears in the corpus — tolerated via `Competitions?`.
    // The slug is the PLURAL "world-rankings-competitions": the annex
    // definition heading is the plural term "World Rankings Competitions",
    // and the FE resolves annex links by `slugify(<definition heading>)`.
    type: 'internal-link',
    pattern: /\bof\s+the\s+World\s+Rankings?\s+Competitions?\s+definition\b/i,
    target: buildAnnexPath('world-rankings-competitions'),
    targetFragment: 'World Rankings Competition',
  },
  {
    // French equivalent: "…de la définition portant sur les Compétitions
    // comptant pour le classement mondial" / "…de la définition des
    // Compétitions comptant pour le classement mondial". Targets the FR
    // annex explicitly via the `?language=FR` search param (read back by
    // `annexLanguageFromExternalUrl`). The slug is derived from the FRENCH
    // term ("Compétitions comptant pour le classement mondial") so it matches
    // `slugify(<FR definition heading>)`, not the English slug.
    type: 'internal-link',
    pattern:
      /\bde\s+la\s+d[ée]finition\s+(?:portant\s+sur\s+les|des)\s+Comp[ée]titions?\s+comptant\s+pour\s+le\s+classement\s+mondial\b/i,
    target: buildAnnexPath(
      'competitions-comptant-pour-le-classement-mondial',
      undefined,
      'language=FR',
    ),
    targetFragment: 'Compétitions comptant pour le classement mondial',
  },
];

/** Strip the `g` flag so match indices are relative to the sliced window. */
function asNonGlobal(re: RegExp): RegExp {
  return re.global ? new RegExp(re.source, re.flags.replace('g', '')) : re;
}

/**
 * Find the cross-book cue (if any) immediately following a reference mention
 * that ends at `fromIndex`. Returns the EARLIEST matching rule so that, were
 * two cues ever to overlap, the one adjacent to the mention wins.
 */
export function findCrossBookCue(
  text: string,
  fromIndex: number,
  rules: readonly CrossBookRule[] = CROSS_BOOK_RULES,
): CrossBookCueMatch | null {
  const slice = text.slice(fromIndex);
  let best: CrossBookCueMatch | null = null;
  for (const rule of rules) {
    const m = slice.match(asNonGlobal(rule.pattern));
    if (m?.index === undefined) continue;
    if (m.index > MAX_CUE_GAP) continue;
    if (!CUE_GAP_RE.test(slice.slice(0, m.index))) continue;
    if (best === null || fromIndex + m.index < best.start) {
      best = {
        rule,
        start: fromIndex + m.index,
        end: fromIndex + m.index + m[0].length,
      };
    }
  }
  return best;
}

/** Targets declared as `internal-link` rules in CROSS_BOOK_RULES. */
export function getKnownInternalLinkTargets(): readonly string[] {
  return CROSS_BOOK_RULES.filter(
    (rule): rule is CrossBookLinkRule => rule.type === 'internal-link',
  ).map(rule => rule.target);
}
